import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { RG_TYPE } from "@project-rouge/rg-core";
import { getRgObjectsOfType, useFunction } from "@project-rouge/rg-three";
import { memo, useMemo } from "react";
import { useMemoState } from "./index38.js";
import { BuildingName } from "./index169.js";
import { BuildingSelector } from "./index170.js";
import { OutClick } from "./index171.js";
import { WingEditor } from "./index172.js";
import { WingInput } from "./index173.js";
import { WingSelector } from "./index174.js";
const topTypes = [RG_TYPE.Facade, RG_TYPE.Parapet, RG_TYPE.Roof];
const detailedViewTopOffset = 1.05;
const abstractViewWingTopOffset = 0.1;
const EditorTools = memo(({
  obj,
  onFloorChange,
  enableKeyboardControls,
  disableKeyboardControls,
  buildingId,
  onBuildingSelectionChange,
  wingLevelsInput = WingInput,
  buildingName = BuildingName,
  visibility,
  aggProps
}) => {
  const f2fHeight = aggProps.dataset.aggConfig.f2fHeight;
  const [zoneToBuilding, buildingToZone, buildingsById] = useMemo(() => {
    const _zoneToBuilding = /* @__PURE__ */ new Map();
    const _buildingToZone = /* @__PURE__ */ new Map();
    const _buildingsById = /* @__PURE__ */ new Map();
    const zones = getRgObjectsOfType(obj, RG_TYPE.Zone);
    for (const zone2 of zones) {
      const buildings = getRgObjectsOfType(zone2, RG_TYPE.Building);
      _zoneToBuilding.set(zone2.uuid, buildings);
      for (const building2 of buildings) {
        _buildingsById.set(building2.uuid, building2);
        _buildingToZone.set(building2.uuid, zone2);
      }
    }
    return [_zoneToBuilding, _buildingToZone, _buildingsById];
  }, [obj]);
  const { zone, building } = useMemo(() => {
    if (!buildingId)
      return {};
    const zone2 = buildingToZone.get(buildingId);
    if (!zone2)
      return {};
    const building2 = buildingsById.get(buildingId);
    if (!building2)
      return {};
    return { zone: zone2, building: building2 };
  }, [buildingId, buildingToZone, buildingsById]);
  const editableBuildings = useMemo(() => {
    const _editableBuildings = /* @__PURE__ */ new Set();
    const editableZones = Object.entries(aggProps.zones).filter(([, zoneProps]) => !zoneProps.frozen).map(([zoneId]) => zoneId);
    for (const zoneId of editableZones) {
      const buildings = zoneToBuilding.get(zoneId);
      if (!buildings)
        continue;
      for (const building2 of buildings) {
        _editableBuildings.add(building2.uuid);
      }
    }
    return _editableBuildings;
  }, [aggProps, zoneToBuilding, obj]);
  const [bone, setBone] = useMemoState(void 0, [aggProps]);
  const topOffset = addTopOffset(visibility) ? detailedViewTopOffset : 0;
  const [floors, setFloors] = useMemoState((bone == null ? void 0 : bone.floors) || 0, [bone]);
  const deselect = useFunction(() => {
    onBuildingSelectionChange(null);
    setBone(void 0);
  });
  const _onFloorChange = useFunction((props) => {
    deselect();
    onFloorChange(props);
  });
  const onBuildingSelect = useFunction((buildingId2) => {
    if (building && bone && bone.floors !== floors) {
      _onFloorChange({ floors, boneId: bone.id, buildingId: building.uuid });
    }
    setBone(void 0);
    onBuildingSelectionChange(buildingId2);
  });
  const aggZoneProps = zone && aggProps.zones[zone.uuid];
  const aggPodiumProps = aggZoneProps == null ? void 0 : aggZoneProps.podium;
  const minFloorNumber = aggPodiumProps ? aggPodiumProps.count + 1 : 1;
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      BuildingSelector,
      {
        obj,
        onSelect: onBuildingSelect,
        buildingName,
        topOffset,
        skipBuildingId: building == null ? void 0 : building.uuid
      }
    ),
    building && !bone && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(OutClick, { onClick: deselect }),
      /* @__PURE__ */ jsx(
        WingSelector,
        {
          obj: building,
          onSelect: setBone,
          topOffset: topOffset || abstractViewWingTopOffset,
          readonly: !editableBuildings.has(building.uuid)
        },
        building.uuid
      )
    ] }),
    building && bone && /* @__PURE__ */ jsx(
      WingEditor,
      {
        bone,
        onAccept: _onFloorChange,
        onCancel: deselect,
        building,
        enableKeyboardControls,
        disableKeyboardControls,
        wingLevelsInput,
        f2fHeight,
        topOffset,
        minFloorNumber,
        floors,
        setFloors
      },
      building.uuid
    )
  ] });
});
EditorTools.displayName = "WingEditorWrapper";
function addTopOffset(visibility) {
  for (const type of topTypes) {
    if (visibility.includes(type))
      return true;
  }
  return false;
}
export {
  EditorTools
};
//# sourceMappingURL=index35.js.map
